const API_BASE = process.env.REACT_APP_API_BASE_URL;

async function parseApiResponse(response) {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    let message = data?.detail || "The request could not be completed.";
    if (Array.isArray(message)) message = message.join(", ");

    const errorMap = data?.errors || data;
    if (errorMap && typeof errorMap === "object") {
      const firstError = Object.entries(errorMap)
        .filter(([key]) => key !== "detail")
        .flatMap(([key, value]) => {
          if (Array.isArray(value)) return value.map(item => `${key}: ${item}`);
          if (typeof value === "string") return [`${key}: ${value}`];
          return [];
        }).find(Boolean);

      if (firstError) {
        message = String(firstError);
      }
    }

    const error = new Error(message);
    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
}

async function parseBlobResponse(response) {
  if (!response.ok) {
    let data = {};

    try {
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = {
          detail: text || "The document could not be retrieved.",
        };
      }
    } catch {
      data = {};
    }

    const error = new Error(
      data?.detail || "The document could not be retrieved.",
    );

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return response.blob();
}

function getManualEntryBaseUrl(loanCaseId) {
  if (!loanCaseId) {
    throw new Error("A loan-case ID is required.");
  }

  return `${API_BASE}/api/lender/loan-cases/${loanCaseId}/manual-entry`;
}

function normalizeApplicantRole(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

/* =========================================================
   MANUAL LOAN CASE
========================================================= */

export async function fetchManualLoanCase({ loanCaseId, accessToken }) {
  const response = await fetch(`${getManualEntryBaseUrl(loanCaseId)}/`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return parseApiResponse(response);
}

/**
 * Saves every populated manual-entry section in one request.
 *
 * Expected payload example:
 *
 * {
 *   manual_entry_current_step: "DOCUMENTS",
 *   personal_info: {...},
 *   financial_info: {
 *     primary_applicant: {
 *       dossier_applicant_id: 1,
 *       employment_status: "EMPLOYED"
 *     },
 *     co_applicant: {
 *       dossier_applicant_id: 2,
 *       employment_status: "EMPLOYED"
 *     },
 *     available_renovation_budget: "50000.00"
 *   },
 *   documents: {...}
 * }
 */
export async function saveManualLoanCaseDraft({
  loanCaseId,
  payload,
  accessToken,
}) {
  const response = await fetch(`${getManualEntryBaseUrl(loanCaseId)}/`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload || {}),
  });

  return parseApiResponse(response);
}

export async function finalizeManualLoanCase({
  loanCaseId,
  payload,
  accessToken,
}) {
  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/finalize/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
    },
  );

  return parseApiResponse(response);
}

/* =========================================================
   MANUAL-ENTRY DOCUMENTS
========================================================= */

export async function uploadManualLoanCaseDocument({
  loanCaseId,
  documentTypeCode,
  dossierApplicantId = null,
  applicantRole = "",
  file,
  accessToken,
}) {
  if (!documentTypeCode) {
    throw new Error("A document-type code is required.");
  }

  if (!(file instanceof File)) {
    throw new Error("A valid file is required.");
  }

  const formData = new FormData();

  formData.append("document_type_code", documentTypeCode);

  if (dossierApplicantId !== null && dossierApplicantId !== undefined) {
    formData.append("dossier_applicant_id", String(dossierApplicantId));
  }

  const normalizedApplicantRole = normalizeApplicantRole(applicantRole);

  if (normalizedApplicantRole) {
    formData.append("applicant_role", normalizedApplicantRole);
  }

  formData.append("file", file, file.name);

  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/documents/upload/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  return parseApiResponse(response);
}

/**
 * Removes an uploaded manual-entry document.
 *
 * document_id is the DossierDocumentRequirement ID returned by the
 * backend. dossier_applicant_id/applicant_role disambiguate applicant-
 * scoped document types when more than one borrower has the same type.
 */
export async function deleteManualLoanCaseDocument({
  loanCaseId,
  documentTypeCode,
  dossierApplicantId = null,
  applicantRole = "",
  documentId = null,
  versionId = null,
  accessToken,
}) {
  if (!documentTypeCode) {
    throw new Error("A document-type code is required.");
  }

  const encodedDocumentTypeCode = encodeURIComponent(documentTypeCode);
  const normalizedApplicantRole = normalizeApplicantRole(applicantRole);

  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/documents/${encodedDocumentTypeCode}/`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        document_id: documentId || null,
        version_id: versionId || null,
        dossier_applicant_id: dossierApplicantId || null,
        applicant_role: normalizedApplicantRole || "",
      }),
    },
  );

  return parseApiResponse(response);
}

/**
 * Retrieves the protected document file as a Blob.
 * Used for document download and inline previews.
 */
export async function fetchLenderDocumentBlob({ versionId, accessToken }) {
  if (!versionId) {
    throw new Error("A document-version ID is required.");
  }

  const response = await fetch(
    `${API_BASE}/api/lender/documents/versions/${versionId}/file/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  return parseBlobResponse(response);
}

/* =========================================================
   MANUAL-ENTRY PROPERTY PHOTOS
========================================================= */

export async function uploadManualPropertyPhotos({
  loanCaseId,
  files,
  accessToken,
}) {
  const selectedFiles = Array.from(files || []);

  if (!selectedFiles.length) {
    throw new Error("At least one property photo is required.");
  }

  const formData = new FormData();

  selectedFiles.forEach((file) => {
    if (!(file instanceof File)) {
      throw new Error("Every property photo must be a valid file.");
    }

    formData.append("files", file, file.name);
  });

  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/property/photos/`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  return parseApiResponse(response);
}

export async function deleteManualPropertyPhoto({
  loanCaseId,
  photoId,
  accessToken,
}) {
  if (!photoId) {
    throw new Error("A property-photo ID is required.");
  }

  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/property/photos/${photoId}/`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  return parseApiResponse(response);
}

export async function fetchManualPropertyPhotoBlob({
  loanCaseId,
  photoId,
  accessToken,
}) {
  if (!photoId) {
    throw new Error("A property-photo ID is required.");
  }

  const response = await fetch(
    `${getManualEntryBaseUrl(loanCaseId)}/property/photos/${photoId}/file/`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  return parseBlobResponse(response);
}
